/**
 * IndexedDB schema for the field tool.
 *
 * Object stores:
 *   - session:        single-row auth record (Cognito tokens or local legacy token)
 *   - jobs:           today's pre-fetched job roster
 *   - submissions:    one in-progress FieldReport per JobID (the inspector's
 *                     working draft); persisted on every field write so a
 *                     crash loses nothing
 *   - photos:         photo blobs, keyed by uuid, indexed by jobId
 *   - syncQueue:      pending uploads (submissions + photos), drained by the
 *                     sync worker when the device is online
 *   - clauseLibrary:  cached clause library JSON (fetched on first online run)
 *
 * Schema version starts at 1. Migrations go in `openDb()`'s upgrade callback.
 */

import type { DBSchema } from 'idb';
import type {
  BatteryChecklistKind,
  FieldReport,
  InspectorId,
  PhotoQuarterTurn,
  PhotoRotationDegrees,
} from '@cer-autofill/shared';

export interface FieldDbSchema extends DBSchema {
  session: {
    key: 'current';
    value: {
      id: 'current';
      source?: 'cognito' | 'legacy';
      displayName?: string;
      email?: string;
      idToken?: string;
      accessToken?: string;
      refreshToken?: string;
      expiresAt?: number;
      deviceToken?: string;
      deviceId: string;
      inspectorId: InspectorId;
      pairedAt: string; // ISO
    };
  };
  jobs: {
    key: string; // jobId
    value: {
      jobId: string;
      cachedAt: string;
      siteAddress?: string;
      tenderer?: string;
      scheduledDate?: string; // ISO date
      inspectionType: 'pv' | 'battery' | 'combo';
      batteryChecklistKind?: BatteryChecklistKind;
      /** Cached trial switch for the BTY location tap. Absent means off. */
      batteryLocationCapture?: boolean;
      /** The other JobID when this row is a combo. */
      comboPartnerJobId?: string;
      bookingNotes?: string;
      // Folded forward — anything the backend wants the PWA to preload
      // (homeowner name candidate, etc.) goes here as it grows.
    };
  };
  submissions: {
    key: string; // jobId
    value: {
      jobId: string;
      updatedAt: string;
      submission: FieldReport;
      // 'draft' while the inspector is working; 'pending' once they tap
      // Submit and we're waiting for the queue to drain; 'sent' after ack.
      status: 'draft' | 'pending' | 'sent' | 'failed';
      // Server ack timestamp once status = 'sent'.
      ackedAt?: string;
      // Last error if status = 'failed'.
      lastError?: string;
    };
    indexes: { 'by-status': 'draft' | 'pending' | 'sent' | 'failed' };
  };
  photos: {
    key: string; // photo uuid
    value: {
      id: string; // matches FieldPhoto.id
      jobId: string;
      promptKey: string;
      filename: string;
      mimeType: string;
      capturedAt: string;
      /**
       * The bytes. Optional because an uploaded photo's blob is eventually
       * released — see `prunePhotoBlobs`. The row itself is kept forever so
       * the UI can still show that the photo exists and what it is filed as;
       * only the (large) bytes go. `blobReleasedAt` records when.
       */
      blob?: Blob;
      /** WebKit fallback: materialised bytes instead of a Blob backing-file handle. */
      blobBytes?: ArrayBuffer;
      blobStorage?: 'bytes';
      blobReleasedAt?: string;
      /**
       * Shared by every photo brought in by one bulk import. Sent on upload so
       * the audit trail can show a 60-photo import as one entry rather than 60
       * unrelated rows — each upload is its own HTTP request, so the server's
       * per-request correlation id cannot do that job.
       */
      importBatchId?: string;
      /**
       * Pixel height of the evidence strip stamped under a new capture.
       * Sent with the upload so a later rotate can trust it.
       */
      evidenceFooterPx?: number;
      /** Net clockwise turn baked into the local blob. Absent until the first rotate. */
      rotationDegrees?: PhotoRotationDegrees;
      /** Bumped when the bytes change so the preview can drop a stale object URL. */
      contentRev?: number;
      /**
       * Server object the last successful upload or rotate stored. A queued
       * rotate names this pair so a retry cannot turn the photo a second time.
       */
      serverStorageKey?: string;
      serverUploadedAt?: string;
      /** Shown when a queued rotate could not be applied and the inspector should try again. */
      rotateNotice?: string;
      uploadStatus: 'pending' | 'uploading' | 'uploaded' | 'failed';
      uploadedAt?: string;
      lastError?: string;
    };
    indexes: { 'by-job': string; 'by-status': string };
  };
  syncQueue: {
    key: string; // <kind>:<id>
    value:
      | {
          id: string;
          kind: 'submission';
          jobId: string;
          enqueuedAt: string;
          attempts: number;
        }
      | {
          id: string;
          kind: 'photo';
          photoId: string;
          jobId: string;
          enqueuedAt: string;
          attempts: number;
        }
      /**
       * A photo already uploaded, then re-filed under a different promptKey.
       * Only ever enqueued for a photo the server has: an un-uploaded photo
       * needs no re-key call, because its pending upload will carry the new
       * key on its own.
       */
      | {
          id: string;
          kind: 'rekey';
          photoId: string;
          jobId: string;
          promptKey: string;
          enqueuedAt: string;
          attempts: number;
        }
      /**
       * A photo already uploaded, then turned. Only enqueued once the server
       * has the file. Repeated turns before a sync collapse into this one item;
       * a net angle of 0 deletes it. `degrees` is the cumulative clockwise turn
       * still owed to the server. `baseStorageKey` and `baseUploadedAt` are the
       * server record that turn was counted from, kept from the first enqueue
       * so later collapses stay relative to the same photo.
       */
      | {
          id: string;
          kind: 'rotate';
          photoId: string;
          jobId: string;
          degrees: PhotoQuarterTurn;
          enqueuedAt: string;
          attempts: number;
          baseStorageKey?: string;
          baseUploadedAt?: string;
        };
  };
  clauseLibrary: {
    key: 'current';
    value: {
      id: 'current';
      cachedAt: string;
      payload: unknown; // ClauseLibrary[] verbatim, validated on read
    };
  };
}

export const DB_NAME = 'cer-field';
export const DB_VERSION = 1;
